import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatDisplayName } from '@/lib/pupils';
import {
  normaliseGrade,
  getTermFromDate,
  type SavedAssessment,
} from '@/lib/progress';
import { PROGRESS_ASSESSMENTS_KEY } from './useAssessments';

export function useProgressAssessments(
  userId: string | undefined,
  isAdmin: boolean,
) {
  return useQuery<SavedAssessment[]>({
    queryKey: [PROGRESS_ASSESSMENTS_KEY, userId, isAdmin],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async () => {
      let savedQuery = supabase
        .from('saved_assessments')
        .select('id, child_name, year_group, grade, assessment_text, term, created_at, assessed_by')
        .order('created_at', { ascending: false });
      if (!isAdmin) {
        savedQuery = savedQuery.eq('assessed_by', userId!);
      }

      let structuredQuery = supabase
        .from('assessments')
        .select(`
          id, assessed_by, ai_grade, final_grade, justification, status, created_at, fluency_score,
          writing_samples!inner(
            pupils!inner(
              first_name, last_name, anonymisation,
              classes!inner(
                name,
                year_groups!inner(
                  name,
                  key_stages!inner( name )
                )
              )
            )
          )
        `)
        .in('status', ['accepted', 'modified'])
        .order('created_at', { ascending: false });
      if (!isAdmin) {
        structuredQuery = structuredQuery.eq('assessed_by', userId!);
      }

      const [savedResult, structuredResult] = await Promise.all([
        savedQuery,
        structuredQuery,
      ]);

      if (savedResult.error)    throw savedResult.error;
      if (structuredResult.error) throw structuredResult.error;

      const savedMapped: SavedAssessment[] = (savedResult.data ?? []).map((a) => ({
        ...a,
        grade: normaliseGrade(a.grade),
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const structuredMapped: SavedAssessment[] = (structuredResult.data ?? []).map((a: any) => {
        const pupil = a.writing_samples?.pupils;
        return {
          id: a.id,
          child_name: pupil
            ? formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation)
            : 'Unknown',
          year_group: pupil?.classes?.year_groups?.name ?? '',
          class_name: pupil?.classes?.name ?? '',
          key_stage_name: pupil?.classes?.year_groups?.key_stages?.name ?? '',
          grade: normaliseGrade(a.final_grade ?? a.ai_grade),
          assessment_text: a.justification ?? '',
          term: getTermFromDate(a.created_at),
          created_at: a.created_at,
          assessed_by: a.assessed_by,
          fluency_score: a.fluency_score ?? null,
        };
      });

      return [...savedMapped, ...structuredMapped].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      );
    },
  });
}
